import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { GuestUserDto, RetrieveProfileResponseDto, UpdateProfileBodyDto } from '@picflow/types';
import { setUser } from '@sentry/angular-ivy';
import { BehaviorSubject, Observable, Subject, lastValueFrom } from 'rxjs';
import { GROWTHBOOK } from '../growthbook';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { GuestAuthService } from './guest-auth.service';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root',
})
export class GuestService {
  private _updated: Subject<boolean> = new BehaviorSubject<boolean>(undefined);
  public readonly onUpdated: Observable<boolean> = this._updated.asObservable();

  private growthBook = inject(GROWTHBOOK);
  private state = inject(StateService);
  private http = inject(HttpClient);
  private loader = inject(DataLoaderService);
  private config = inject(AppConfigService);
  private guestAuth = inject(GuestAuthService);
  private platformId = inject(PLATFORM_ID);

  async getGuest(): Promise<GuestUserDto> {
    if (isPlatformServer(this.platformId)) return;
    try {
      const guest = await this.loader.get('guest', () => this.getGuestInternal());
      if (guest) setUser({ id: guest['id'] });
      return guest;
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      return undefined;
    }
  }

  private async getGuestInternal() {
    await this.guestAuth.init();
    if (!this.state.guestAuthToken) return;
    const resp = <RetrieveProfileResponseDto>(
      await lastValueFrom(this.http.get(`${this.config.apiEndpoint}/profile?force-guest=true`))
    );
    this.state.guest = <GuestUserDto>resp.user;
    this.growthBook.setAttributes({
      ...this.growthBook.getAttributes(),
      guestId: this.state.guest['id'],
    });
    return this.state.guest;
  }

  async updateGuest(data: UpdateProfileBodyDto): Promise<void> {
    await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/profile`, data));
    this.loader.remove('guest');
    await this.getGuest();
    this._updated.next(true);
  }
}
